import { openDB, deleteDB, type IDBPDatabase, type DBSchema } from 'idb';
import { randomBytes, utf8 } from '@holdthat/crypto';

interface Sealed { nonce: Uint8Array<ArrayBuffer>; ciphertext: ArrayBuffer }
interface VaultDB extends DBSchema { keys: { key: string; value: CryptoKey }; records: { key: string; value: Sealed } }
/** Device protection also applies to Standard data. No decrypted UI models on disk. */
export class Vault {
  private database?: Promise<IDBPDatabase<VaultDB>>;
  private keyPromise?: Promise<CryptoKey>;
  private tail: Promise<unknown> = Promise.resolve();
  readonly name: string;
  constructor(readonly userID: string) { this.name = `holdthat-v3-web-${userID}`; }
  private db() { return this.database ??= openDB<VaultDB>(this.name, 1, { upgrade(db) { db.createObjectStore('keys'); db.createObjectStore('records'); } }); }
  private key() { return this.keyPromise ??= (async () => {
    const db = await this.db(); const found = await db.get('keys', 'device'); if (found) return found;
    const candidate = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    const tx = db.transaction('keys', 'readwrite'); const concurrent = await tx.store.get('device');
    if (!concurrent) await tx.store.put(candidate, 'device'); await tx.done; return concurrent ?? candidate;
  })(); }
  async readBytes(id: string): Promise<ArrayBuffer | undefined> {
    const record = await (await this.db()).get('records', id); if (!record) return;
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.nonce, additionalData: utf8(`holdthat:web:v3:${this.userID}:${id}`) }, await this.key(), record.ciphertext);
  }
  async writeBytes(id: string, bytes: ArrayBuffer) {
    const nonce = randomBytes(12); const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(`holdthat:web:v3:${this.userID}:${id}`) }, await this.key(), bytes);
    await (await this.db()).put('records', { nonce, ciphertext }, id);
  }
  async get<T>(id: string): Promise<T | undefined> { const bytes = await this.readBytes(id); return bytes ? JSON.parse(new TextDecoder().decode(bytes)) as T : undefined; }
  async set(id: string, value: unknown) { await this.writeBytes(id, new TextEncoder().encode(JSON.stringify(value)).buffer); }
  async remove(id: string) { await (await this.db()).delete('records', id); }
  async keys(prefix: string) { return (await (await this.db()).getAllKeys('records')).filter((key) => key.startsWith(prefix)); }
  async update<T>(id: string, initial: () => T, mutate: (value: T) => void | Promise<void>): Promise<T> {
    const work = async () => { const value = await this.get<T>(id) ?? initial(); await mutate(value); await this.set(id, value); return value; };
    const locked = async () => await (typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(`${this.name}:${id}`, work) : work());
    const result = this.tail.then(locked, locked); this.tail = result.catch(() => undefined); return result;
  }
  async close() { await this.tail; (await this.database)?.close(); this.database = undefined; this.keyPromise = undefined; }
  async destroy() { await this.close(); await deleteDB(this.name); }
}
